import type { ReactNode } from 'react';
import { useSession } from '../auth/session';
import { useRouter } from '../router';
import { useProgress } from './progress';
import { stepBySlug, stepPosition, stepsFor } from './steps';

/**
 * The calm register: step tag, the step's one question as the headline, a
 * short lede, the content, then Back / Continue. Continue marks the step done
 * and moves to the next step this role can see; `beforeContinue` may veto it
 * (e.g. an unsaved draft that fails validation). `blockedLabel` disables
 * Continue outright and says why ("Fix 2 fields to continue") — the same
 * rule that disables Save draft and Publish, so the footer never looks like
 * a way past a form the page itself refuses (third live review, 2026-09-27:
 * it stayed enabled and silently did nothing).
 *
 * "Step n of N" counts the steps this role sees on the rail (setup/steps.ts),
 * so the counter and the rail always agree.
 */
export function OnboardingPage({
  slug,
  question,
  lede,
  children,
  beforeContinue,
  continueLabel = 'Continue',
  blockedLabel,
  aside,
}: {
  slug: string;
  /** In place of the step's own question, when the page's answer changes what it asks (the installer step with a page from us). */
  question?: string;
  lede?: ReactNode;
  children: ReactNode;
  beforeContinue?: () => Promise<boolean> | boolean;
  continueLabel?: string;
  /** Set while the step's form has problems: Continue is disabled and shows this instead. */
  blockedLabel?: string | null;
  aside?: ReactNode;
}) {
  const step = stepBySlug(slug);
  const { role } = useSession();
  const { navigate } = useRouter();
  const { markDone } = useProgress();
  const position = stepPosition(slug, role);
  if (!step) return null;
  const visible = stepsFor(role);
  const idx = visible.findIndex((s) => s.slug === slug);
  const prev = idx > 0 ? visible[idx - 1] : undefined;
  const next = visible[idx + 1];

  const go = async () => {
    if (blockedLabel) return;
    if (beforeContinue && !(await beforeContinue())) return;
    markDone(slug);
    // Forced: `beforeContinue` has just saved, and the page's unsaved-changes
    // guard may not have re-rendered yet.
    navigate(next ? `/setup/${next.slug}` : '/dashboard', { force: true });
  };

  return (
    <div>
      <header className="page-head">
        {position ? (
          <div className="step-tag eyebrow">
            <span>
              Step {position.n} of {position.total} · {step.title}
              {step.optional ? ' · Optional' : ''}
            </span>
            <span className="bar" aria-hidden="true">
              <i style={{ width: `${Math.round((position.n / position.total) * 100)}%` }} />
            </span>
          </div>
        ) : null}
        <h1 id="page-title" tabIndex={-1}>
          {question ?? step.question}
        </h1>
        {lede ? <p className="lede">{lede}</p> : null}
        {step.optionalNote ? <p className="small muted optional-note">{step.optionalNote}</p> : null}
      </header>
      <div className="stack-lg">{children}</div>
      {aside}
      <footer className="onboard-foot">
        {prev ? (
          <button type="button" className="btn btn-ghost" onClick={() => navigate(`/setup/${prev.slug}`)}>
            ← {prev.title}
          </button>
        ) : (
          <span />
        )}
        {blockedLabel ? (
          <button type="button" className="btn btn-primary btn-lg" disabled>
            {blockedLabel}
          </button>
        ) : (
          <button type="button" className="btn btn-primary btn-lg" onClick={() => void go()}>
            {next ? `${continueLabel}` : 'Go to dashboard'}
            <span aria-hidden="true">→</span>
          </button>
        )}
      </footer>
    </div>
  );
}
